import type {
  MpAiBrief,
  MpBenchmark,
  MpBenchRow,
  MpDataStatus,
  MpKpi,
  MpMapPackData,
  MpPlaybookStep,
  MpProfileCompleteness,
  MpProfileItem,
  MpReviewAuditItem,
  MpToneKey,
  MpVectorData,
} from '@elva/ui';
import { growthGet } from '@/data/http';
import type { BeMapPack } from '@/data/marketingApiTypes';
import { deepCamel, num, str } from '@/data/adapt';
import { getErrorStatus } from '@/data/errors';

const EMPTY_MAP_PACK: MpMapPackData = {
  blocked: true,
  dataStatus: 'pipeline_pending',
  kpis: [],
  benchmark: { competitorName: null, insight: null, rows: [] },
  attackVector: null,
  threatVector: null,
  profile: { yours: null, competitor: null },
  reviewAudit: [],
  aiBrief: null,
};

const MP_TONE_ALIASES: Record<string, MpToneKey> = {
  ai: 'ai',
  info: 'info',
  success: 'success',
  gold: 'gold',
  terra: 'terra',
  terracotta: 'terra',
  warning: 'gold',
  error: 'terra',
  danger: 'terra',
};

function adaptTone(value: unknown): MpToneKey {
  const key = str(value, 'info').toLowerCase();
  return MP_TONE_ALIASES[key] ?? 'info';
}

function adaptDataStatus(value: unknown): MpDataStatus | undefined {
  const status = str(value, '').toLowerCase();
  if (status === 'pipeline_ready' || status === 'pipeline_pending') return status;
  return undefined;
}

function adaptCell(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length ? text : null;
}

function adaptWinner(value: unknown): MpBenchRow['winner'] {
  const winner = str(value, '').toLowerCase();
  if (winner === 'you') return 'you';
  if (winner === 'comp') return 'comp';
  return null;
}

function adaptKpi(row: Record<string, unknown>): MpKpi {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    label: str(c.label),
    value: c.value == null ? null : String(c.value),
    tone: adaptTone(c.tone),
    sub: str(c.sub, ''),
    ...(c.small != null ? { small: Boolean(c.small) } : {}),
  };
}

function adaptBenchRow(row: Record<string, unknown>): MpBenchRow {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    metric: str(c.metric),
    yours: adaptCell(c.yours),
    comp: adaptCell(c.comp),
    avg: adaptCell(c.avg),
    winner: adaptWinner(c.winner),
    ...(c.star != null ? { star: Boolean(c.star) } : {}),
  };
}

function adaptBenchmark(be: BeMapPack): MpBenchmark {
  const raw = be.benchmark ?? {};
  const c = deepCamel<Record<string, unknown>>(raw);
  const rows = (raw.rows ?? (raw as { rows?: unknown[] }).rows ?? []) as Array<
    Record<string, unknown>
  >;

  const dataStatus = adaptDataStatus(c.dataStatus ?? be.data_status);

  return {
    competitorName: adaptCell(c.competitorName),
    insight: adaptCell(c.insight),
    ...(dataStatus ? { dataStatus } : {}),
    rows: rows.map(adaptBenchRow),
  };
}

function adaptProfileItem(row: Record<string, unknown>): MpProfileItem {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    label: str(c.label),
    done: Boolean(c.done),
    ...(c.detail ? { detail: str(c.detail) } : {}),
  };
}

function adaptProfile(row: Record<string, unknown> | undefined): MpProfileCompleteness | null {
  if (!row) return null;
  const c = deepCamel<Record<string, unknown>>(row);
  const items = ((row.items ?? []) as Array<Record<string, unknown>>).map(adaptProfileItem);
  const label = c.label == null ? null : str(c.label, '');
  const hasLabel = Boolean(label);
  const hasPercent = c.percent != null && Number.isFinite(Number(c.percent));
  if (!items.length && !hasPercent && !hasLabel) return null;

  return {
    label: label ?? 'Profile',
    percent: hasPercent ? num(c.percent) : 0,
    tone: adaptTone(c.tone),
    items,
  };
}

function adaptDataFreshness(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const text = value.trim();
    return text.length ? text : null;
  }
  if (typeof value === 'object') {
    const c = deepCamel<Record<string, unknown>>(value as Record<string, unknown>);
    if (c.lastSyncedAt) return str(c.lastSyncedAt);
    if (c.status) return str(c.status);
  }
  return null;
}

function adaptVector(row: Record<string, unknown> | undefined): MpVectorData | null {
  if (!row) return null;
  const c = deepCamel<Record<string, unknown>>(row);
  const yourPraise = Array.isArray(c.yourPraise) ? c.yourPraise.map(String).filter(Boolean) : [];
  const theirComplaints = Array.isArray(c.theirComplaints)
    ? c.theirComplaints.map(String).filter(Boolean)
    : [];
  const theirPraise = Array.isArray(c.theirPraise) ? c.theirPraise.map(String).filter(Boolean) : [];
  const yourComplaints = Array.isArray(c.yourComplaints)
    ? c.yourComplaints.map(String).filter(Boolean)
    : [];
  const note = str(c.note, '');

  if (!yourPraise.length && !theirComplaints.length && !theirPraise.length && !yourComplaints.length && !note) {
    return null;
  }

  return {
    ...(yourPraise.length ? { yourPraise } : {}),
    ...(theirComplaints.length ? { theirComplaints } : {}),
    ...(theirPraise.length ? { theirPraise } : {}),
    ...(yourComplaints.length ? { yourComplaints } : {}),
    note,
  };
}

function adaptReviewAudit(row: Record<string, unknown>): MpReviewAuditItem {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    value: str(c.value, '—'),
    tone: adaptTone(c.tone),
    label: str(c.label),
    sub: str(c.sub, ''),
  };
}

function adaptPlaybookStep(row: Record<string, unknown>, index: number): MpPlaybookStep {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    num: num(c.num, index + 1),
    title: str(c.title),
    urgency: str(c.urgency, '—'),
    desc: str(c.desc ?? c.description),
    ...(c.cta ? { cta: str(c.cta) } : {}),
  };
}

function adaptAiBrief(row: Record<string, unknown> | undefined): MpAiBrief | null {
  if (!row) return null;
  const c = deepCamel<Record<string, unknown>>(row);
  const playbook = ((row.playbook ?? []) as Array<Record<string, unknown>>).map(adaptPlaybookStep);
  const bottomLine = adaptCell(c.bottomLine);

  if (!bottomLine && !playbook.length) return null;

  return {
    bottomLine,
    playbook,
  };
}

export function adaptMapPack(be: BeMapPack): MpMapPackData {
  const dataStatus = adaptDataStatus(be.data_status ?? be.benchmark?.data_status);

  return {
    ...(dataStatus ? { dataStatus } : {}),
    ...(adaptDataFreshness(be.data_freshness)
      ? { dataFreshness: adaptDataFreshness(be.data_freshness) }
      : {}),
    ...(be.competitors_benchmarked_count != null
      ? { competitorsBenchmarkedCount: num(be.competitors_benchmarked_count) }
      : {}),
    kpis: (be.kpis ?? []).map((row) => adaptKpi(row as Record<string, unknown>)),
    benchmark: adaptBenchmark(be),
    attackVector: adaptVector(be.attack_vector as Record<string, unknown> | undefined),
    threatVector: adaptVector(be.threat_vector as Record<string, unknown> | undefined),
    profile: {
      yours: adaptProfile(be.profile?.yours as Record<string, unknown> | undefined),
      competitor: adaptProfile(be.profile?.competitor as Record<string, unknown> | undefined),
    },
    reviewAudit: (be.review_audit ?? []).map((row) =>
      adaptReviewAudit(row as Record<string, unknown>),
    ),
    aiBrief: adaptAiBrief(be.ai_brief as Record<string, unknown> | undefined),
  };
}

export async function fetchMapPack(): Promise<MpMapPackData> {
  try {
    const be = await growthGet<BeMapPack>('/map-pack/intelligence/');
    return adaptMapPack(be);
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 500 || status === 502 || status === 503) {
      return { ...EMPTY_MAP_PACK, blocked: true };
    }
    throw error;
  }
}
